import { FormEvent, useEffect, useState } from "react";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminReminderStatus,
  adminRunReminders,
  adminTestReminder,
  adminUpdateSettings,
  type ConferenceReminderStatus,
} from "../api";
import { PageHeader } from "../shells/PageHeader";

export function AdminRemindersPage() {
  const { t } = useLanguage();
  const [status, setStatus] = useState<ConferenceReminderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () =>
    adminReminderStatus()
      .then(setStatus)
      .catch(() => setStatus(null));

  useEffect(() => {
    reload().catch(() => undefined);
  }, []);

  const onToggle = async () => {
    if (!status) return;
    setBusy(true);
    setError(null);
    try {
      await adminUpdateSettings({ reminders_enabled: !status.reminders_enabled });
      await reload();
      setMessage(t.admin.saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onRun = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await adminRunReminders();
      setMessage(
        `${t.admin.remindersRunDone}: sent=${String(result.sent ?? 0)} failed=${String(result.failed ?? 0)}`,
      );
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onTest = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminTestReminder({ email: testEmail.trim() });
      setMessage(t.admin.remindersTestSent);
      setTestEmail("");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  if (!status) return <p>{t.common.loading}</p>;

  return (
    <div>
      <PageHeader title={t.admin.reminders} lead={t.admin.remindersLead} backTo="/admin" />
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {message}
        </div>
      )}

      <div className="dn-grid" style={{ marginBottom: "1.25rem" }}>
        <div className="dn-card">
          <h3>{t.admin.remindersEnabled}</h3>
          <p style={{ fontSize: "1.4rem" }}>
            {status.reminders_enabled ? t.admin.remindersOn : t.admin.remindersOff}
          </p>
          <button type="button" className="dn-btn dn-btn-ghost" disabled={busy} onClick={onToggle}>
            {status.reminders_enabled ? t.admin.remindersDisable : t.admin.remindersEnable}
          </button>
        </div>
        <div className="dn-card">
          <h3>{t.admin.remindersNext}</h3>
          <p>
            {status.next_scheduled
              ? `${status.next_scheduled.scheduled_for} · ${status.next_scheduled.window} · ${status.next_scheduled.days_remaining}d`
              : t.admin.remindersNone}
          </p>
          <p className="dn-muted">
            {t.admin.remindersTimezone}: {status.timezone}
          </p>
        </div>
        <div className="dn-card">
          <h3>{t.admin.remindersTotals}</h3>
          <p>
            {t.admin.remindersSent}: {status.totals.sent} · {t.admin.remindersFailed}:{" "}
            {status.totals.failed}
          </p>
          <p className="dn-muted">
            {t.admin.remindersEligible}: {status.eligible_recipients} · SMTP:{" "}
            {status.smtp_enabled ? "on" : "off"}
          </p>
        </div>
      </div>

      <div className="dn-cta-row" style={{ marginBottom: "1.25rem" }}>
        <button type="button" className="dn-btn dn-btn-primary" disabled={busy} onClick={onRun}>
          {t.admin.remindersRunNow}
        </button>
      </div>

      <form className="dn-card dn-form" onSubmit={onTest} style={{ marginBottom: "1.5rem" }}>
        <h3>{t.admin.remindersTest}</h3>
        <p className="dn-muted">{t.admin.remindersTestHelp}</p>
        <label>
          {t.admin.remindersTestEmail}
          <input
            type="email"
            required
            value={testEmail}
            onChange={(ev) => setTestEmail(ev.target.value)}
            placeholder="you@example.com"
          />
        </label>
        <button className="dn-btn dn-btn-gold" type="submit" disabled={busy}>
          {t.admin.remindersSendTest}
        </button>
      </form>

      <h2>{t.admin.remindersRecent}</h2>
      <div className="dn-table-wrap">
        <table className="dn-table dn-table-responsive">
          <thead>
            <tr>
              <th>{t.admin.colStatus}</th>
              <th>Email</th>
              <th>{t.admin.remindersWindow}</th>
              <th>{t.admin.remindersScheduled}</th>
              <th>{t.admin.remindersSentAt}</th>
            </tr>
          </thead>
          <tbody>
            {status.recent.length === 0 && (
              <tr>
                <td colSpan={5}>{t.admin.remindersNone}</td>
              </tr>
            )}
            {status.recent.map((r) => (
              <tr key={r.id}>
                <td data-label={t.admin.colStatus}>
                  {r.status}
                  {r.error_message ? ` · ${r.error_message}` : ""}
                </td>
                <td data-label="Email">{r.email}</td>
                <td data-label={t.admin.remindersWindow}>
                  {r.window} / {r.period_key}
                </td>
                <td data-label={t.admin.remindersScheduled}>{r.scheduled_for || "—"}</td>
                <td data-label={t.admin.remindersSentAt}>{r.sent_at || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
